import { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type ContentRestrictionLevel = 'unrestricted' | 'moderate' | 'strict';

interface ParentalControlsSettings {
  enabled: boolean;
  pinHash: string | null;
  contentRestrictionLevel: ContentRestrictionLevel;
  chatEnabled: boolean;
  userGeneratedContentEnabled: boolean;
  profileVisibilityPublic: boolean;
}

interface ParentalControlsContextType {
  settings: ParentalControlsSettings;
  isEnabled: boolean;
  isPinSet: boolean;
  isUnlocked: boolean;
  unlock: (pin: string) => boolean;
  lock: () => void;
  setupPin: (pin: string) => void;
  changePin: (oldPin: string, newPin: string) => boolean;
  removePin: (pin: string) => boolean;
  updateSettings: (updates: Partial<ParentalControlsSettings>) => void;
  isChatAllowed: boolean;
  isUGCAllowed: boolean;
}

const STORAGE_KEY = 'parental_controls';

const DEFAULT_SETTINGS: ParentalControlsSettings = {
  enabled: false,
  pinHash: null,
  contentRestrictionLevel: 'unrestricted',
  chatEnabled: true,
  userGeneratedContentEnabled: true,
  profileVisibilityPublic: true,
};

function hashPin(pin: string): string {
  let hash = 0;
  for (let i = 0; i < pin.length; i++) {
    const char = pin.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return 'ph_' + Math.abs(hash).toString(36);
}

function loadSettings(): ParentalControlsSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore parse errors
  }
  return { ...DEFAULT_SETTINGS };
}

function saveSettings(settings: ParentalControlsSettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

const ParentalControlsContext = createContext<ParentalControlsContextType | undefined>(undefined);

export function ParentalControlsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<ParentalControlsSettings>(loadSettings);
  const [isUnlocked, setIsUnlocked] = useState(false);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const isEnabled = settings.enabled;
  const isPinSet = !!settings.pinHash;

  const unlock = useCallback((pin: string): boolean => {
    if (!settings.pinHash) return false;
    if (hashPin(pin) === settings.pinHash) {
      setIsUnlocked(true);
      return true;
    }
    return false;
  }, [settings.pinHash]);

  const lock = useCallback(() => {
    setIsUnlocked(false);
  }, []);

  const setupPin = useCallback((pin: string) => {
    setSettings(prev => {
      const updated = { ...prev, pinHash: hashPin(pin), enabled: true };
      return updated;
    });
    setIsUnlocked(true);
  }, []);

  const changePin = useCallback((oldPin: string, newPin: string): boolean => {
    if (!settings.pinHash || hashPin(oldPin) !== settings.pinHash) return false;
    setSettings(prev => ({ ...prev, pinHash: hashPin(newPin) }));
    return true;
  }, [settings.pinHash]);

  const removePin = useCallback((pin: string): boolean => {
    if (!settings.pinHash || hashPin(pin) !== settings.pinHash) return false;
    setSettings(prev => ({
      ...prev,
      pinHash: null,
      enabled: false,
      contentRestrictionLevel: 'unrestricted',
      chatEnabled: true,
      userGeneratedContentEnabled: true,
      profileVisibilityPublic: true,
    }));
    setIsUnlocked(false);
    return true;
  }, [settings.pinHash]);

  const updateSettings = useCallback((updates: Partial<ParentalControlsSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  const isChatAllowed = !isEnabled || settings.chatEnabled;
  const isUGCAllowed = !isEnabled || settings.userGeneratedContentEnabled;

  return (
    <ParentalControlsContext.Provider
      value={{
        settings,
        isEnabled,
        isPinSet,
        isUnlocked,
        unlock,
        lock,
        setupPin,
        changePin,
        removePin,
        updateSettings,
        isChatAllowed,
        isUGCAllowed,
      }}
    >
      {children}
    </ParentalControlsContext.Provider>
  );
}

export function useParentalControls() {
  const context = useContext(ParentalControlsContext);
  if (context === undefined) {
    throw new Error('useParentalControls must be used within a ParentalControlsProvider');
  }
  return context;
}
